import { memo, useCallback } from 'react'
import { interaction, isWeb, Stack, XStack, YStack } from '@mythical/ui'
import { RemoveScroll } from 'app/components/primitive/RemoveScroll'
import { useNavigationStore } from './NavigationStore'
import { GestureHandlerScrollView } from '../GestureHandlerScrollView'
import { BlurView } from 'expo-blur'
import { useSafeArea } from 'app/provider/safe-area/use-safe-area'
import { useOnKeyPress } from 'app/hooks/use-on-key-press'
import { LogoSmall } from './Logo'
import {
  Button,
  ButtonLink,
  ButtonProps,
} from 'app/components/primitive/Button'
import {
  IconClose,
  IconCollectibles,
  IconSearch,
} from 'app/components/primitive/Icon'
import {
  IconBookmark,
  IconChat,
  IconCog,
  IconExternalLink,
  IconGift,
  IconHeart,
  IconHome,
  IconLogOut,
  IconPlaylist,
  IconUser,
} from 'app/components/primitive/Icon'
import { NAV_HEIGHT, pageLinks, SIDEBAR_WIDTH } from './constants'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { Avatar } from 'app/components/primitive/Avatar'
import Version from 'app/components/primitive/Version/index'
import { useCurrentPath } from 'app/hooks/use-current-path'
import getEnv from 'app/lib/env-getter'
import { useLogout } from 'app/hooks/use-logout'
import { useStores } from 'app/store'

type NavLink = {
  label: string
  href: string
  icon: any
  isLoggedIn?: boolean
  match?: string[]
  hidden?: boolean
}

const navLinks: NavLink[] = [
  {
    label: 'Home',
    icon: <IconHome size={24} />,
    href: isWeb ? '/' : '/homescreen',
    match: ['/', '/homescreen'],
  },
  {
    label: 'Search',
    href: '/search',
    icon: <IconSearch size={24} />,
    hidden: !isWeb,
  },
  {
    label: 'Videos',
    href: '/content',
    icon: <IconPlaylist size={24} />,
  },
  {
    label: 'Merch & Collectibles',
    href: '/merch',
    icon: <IconGift size={24} />,
  },
  {
    label: 'My Collectibles',
    href: '/my-collectibles',
    icon: <IconCollectibles size={24} />,
    isLoggedIn: true,
  },
  {
    label: 'Watch Later',
    href: '/watch-later',
    icon: <IconBookmark size={24} />,
    isLoggedIn: true,
  },
  {
    label: 'Favorites',
    href: '/favorites',
    icon: <IconHeart size={24} />,
    isLoggedIn: true,
  },
  {
    label: 'Comments',
    href: '/user/comments',
    icon: <IconChat size={24} />,
    isLoggedIn: true,
  },
  {
    label: 'Settings',
    href: '/user/settings',
    icon: <IconCog size={24} />,
    isLoggedIn: true,
  },
]

type MobileNavContentProps = {
  onNavigate?: () => void
}

const MobileNavContent = ({ onNavigate }: MobileNavContentProps) => {
  const { path } = useCurrentPath()
  const { userStore } = useStores()
  const isLoggedIn = !!userStore?.user?.id
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  const logout = useLogout()
  const handleLogout = useCallback(() => {
    onNavigate?.()
    logout()
  }, [onNavigate, logout])

  return (
    <YStack gap="$3.5" pb="$4" w="100%" h="100%" px="$3.5">
      <YStack
        flexGrow={1}
        gap="$3"
        width="100%"
        mx="auto"
        maxWidth={SIDEBAR_WIDTH}
      >
        {isLoggedIn && (
          <NavButton
            isActive
            onNavigate={onNavigate}
            h={56}
            link={{
              label: 'Profile',
              href: '/user/profile',
              icon: (
                <Avatar
                  src={userStore?.user?.profileImage}
                  width={32}
                  height={32}
                  bw={2}
                  boc="$fgPrimary"
                />
              ),
            }}
          />
        )}

        <YStack gap="$1.5">
          {navLinks
            .filter((link) => !link.isLoggedIn || isLoggedIn)
            .filter((link) => !link.hidden)
            .map((link) => (
              <NavButton
                key={link.label}
                link={link}
                isActive={
                  path === link.href || (link.match?.includes(path) ?? false)
                }
                onNavigate={onNavigate}
              />
            ))}
          {getEnv() !== 'production' && (
            <NavButton
              link={{
                label: 'Mock Login',
                href: '/mock-login',
                icon: <IconUser size={24} />,
              }}
              isActive={path === '/mock-login'}
              onNavigate={onNavigate}
            />
          )}
        </YStack>

        <YStack mt="auto" gap="$3.5">
          <YStack gap="$2">
            {isLoggedIn && (
              <Button
                onPress={handleLogout}
                iconAfter={<IconLogOut size={18} />}
                theme="bgPrimaryOutlined"
                br="$pill"
                size="$4"
                fontFamily="$title"
                fontSize="$m"
                bw={2}
              >
                Sign out
              </Button>
            )}
            <ButtonLink
              href="https://mythical.com"
              onPressCustom={() => onNavigate?.()}
              iconAfter={<IconExternalLink size={18} />}
              theme="secondary"
              br="$pill"
              size="$4"
              fontFamily="$title"
              fontSize="$m"
            >
              Mythical.com
            </ButtonLink>
          </YStack>

          <XStack mx="auto" mt="auto" flexWrap="wrap" jc="center" gap="$2">
            {pageLinks
              .filter((link) =>
                link.href === '/perks' ? canMemberfulLink : true,
              )
              .map((link) => (
                <ButtonLink
                  key={link.label}
                  href={link.href}
                  size="$2"
                  fontSize="$2"
                  onPressCustom={onNavigate}
                  theme="secondary"
                >
                  {link.label}
                </ButtonLink>
              ))}
          </XStack>
          <Version />
        </YStack>
      </YStack>
    </YStack>
  )
}

export const MobileNavigationMenu = memo(() => {
  const setNavOpen = useNavigationStore((state) => state.setNavOpen)
  const navOpen = useNavigationStore((state) => state.navOpen)
  const insets = useSafeArea()

  const handleCloseSidebar = useCallback(() => {
    setNavOpen(false)
  }, [])

  useOnKeyPress('Escape', handleCloseSidebar)

  return (
    <RemoveScroll enabled={navOpen}>
      <YStack
        id="sidebar"
        key="sidebar"
        zIndex="$6"
        pos={isWeb ? ('fixed' as 'absolute') : 'absolute'}
        height="100%"
        maxWidth={SIDEBAR_WIDTH}
        w="85%"
        right={0}
        top={0}
        bg="$bgPrimary"
        gap="$3.5"
        pt={insets.top}
        $gtMd={{ display: 'none' }}
        x={navOpen ? 0 : SIDEBAR_WIDTH}
        o={navOpen ? 1 : 0}
        animation="medium"
        {...(!isWeb && {
          animateOnly: ['transform', 'opacity'],
        })}
      >
        <XStack jc="space-between" ai="center" px="$5" h={NAV_HEIGHT}>
          <SmartLink
            href={isWeb ? '/' : '/homescreen'}
            Component={LogoSmall}
            onPress={handleCloseSidebar}
            {...interaction.opacity}
          />

          <Button
            aria-label="Close sidebar"
            theme="secondary"
            onPress={handleCloseSidebar}
            icon={<IconClose size={24} />}
            size="$3.5"
            circular
          />
        </XStack>

        <GestureHandlerScrollView
          contentContainerStyle={{
            paddingBottom: insets.bottom,
            flexGrow: 1,
          }}
        >
          <MobileNavContent onNavigate={handleCloseSidebar} />
        </GestureHandlerScrollView>
      </YStack>

      {navOpen && (
        <Stack
          key="sidebar-overlay"
          tag="button"
          aria-label="Close sidebar"
          onPress={handleCloseSidebar}
          pos={isWeb ? ('fixed' as 'absolute') : 'absolute'}
          w="100%"
          h="100%"
          zi="$5"
          top="$0"
          animation="quick"
          $gtMd={{ display: 'none' }}
          animateOnly={['opacity']}
          enterStyle={{ o: 0 }}
          exitStyle={{ o: 0 }}
        >
          <BlurView
            style={{
              width: '100%',
              height: '100%',
              zIndex: 100,
            }}
            tint="dark"
            experimentalBlurMethod="dimezisBlurView"
            intensity={40}
          />
        </Stack>
      )}
    </RemoveScroll>
  )
})

type NavButtonProps = {
  link: NavLink
  isActive: boolean
  onNavigate?: () => void
  external?: boolean
} & ButtonProps

const NavButton = ({
  link,
  isActive,
  onNavigate,
  external,
  ...props
}: NavButtonProps) => {
  const Icon = link.icon

  return (
    <ButtonLink
      theme={isActive ? 'bgSecondary' : 'bgPrimary'}
      size="$4"
      fontFamily="$title"
      fontSize="$m"
      px="$3"
      br="$5"
      jc="flex-start"
      bw={0}
      h={50}
      icon={Icon}
      {...(external && { target: '_blank' })}
      {...props}
      href={link.href}
      onPressCustom={() => onNavigate?.()}
    >
      {link.label}
    </ButtonLink>
  )
}
